import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  FaWallet,
  FaRupeeSign,
  FaArrowUp,
  FaArrowDown,
  FaExclamationTriangle,
  FaCheckCircle,
  FaClock,
  FaEye,
  FaEyeSlash,
  FaHistory,
} from "react-icons/fa";
import {
  requestWithdrawal,
  payCommission,
} from "../../features/wallet/walletSlice";
import WithdrawalModal from "./WithdrawalModal";
import CommissionPaymentModal from "./CommissionPaymentModal";

const WalletCard = ({ wallet, driverId, onRefresh }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [showBalance, setShowBalance] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Get booking history from Redux to calculate accurate total earnings
  const { bookingHistory } = useSelector((state) => state.driver);

  // Track if we've attempted to load wallet data
  useEffect(() => {
    if (wallet || (!wallet && driverId)) {
      // If we have wallet data or if we have a driverId (meaning we've tried to load)
      setIsInitialLoad(false);
    }
  }, [wallet, driverId]);

  // Add real-time socket listeners for wallet updates
  useEffect(() => {
    const socket = window.io && window.io(); // Use global socket if available

    if (socket && driverId) {
      // Join driver room for real-time updates
      socket.emit('joinDriverRoom', driverId);

      // Listen for wallet updates for this specific driver
      const handleWalletUpdate = (data) => {
        if (data.driverId === driverId && onRefresh) {
          // Refresh wallet data when update is received
          onRefresh();
        }
      };

      // Subscribe to wallet events
      socket.on('walletUpdated', handleWalletUpdate);
      socket.on('commissionProcessed', handleWalletUpdate);
      socket.on('withdrawalUpdated', handleWalletUpdate);

      // Cleanup
      return () => {
        socket.off('walletUpdated', handleWalletUpdate);
        socket.off('commissionProcessed', handleWalletUpdate);
        socket.off('withdrawalUpdated', handleWalletUpdate);
      };
    }
  }, [driverId, onRefresh]);

  // Only show skeleton during initial load, not when wallet is permanently null
  if (!wallet && isInitialLoad) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 animate-pulse">
        <div className="flex items-center justify-between mb-4">
          <div className="h-6 w-32 bg-gray-200 rounded"></div>
          <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
        </div>
        <div className="h-8 w-24 bg-gray-200 rounded mb-2"></div>
        <div className="h-4 w-full bg-gray-200 rounded"></div>
      </div>
    );
  }

  // If wallet data failed to load, show error state instead of skeleton
  if (!wallet) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
        <div className="text-center py-4">
          <FaWallet className="mx-auto text-3xl text-gray-400 mb-2" />
          <p className="text-gray-500 mb-3">Unable to load wallet information</p>
          {onRefresh && (
            <button
              onClick={onRefresh}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              Try Again
            </button>
          )}
        </div>
      </div>
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  // Calculate commission breakdown from wallet transactions
  const calculateCommissionBreakdown = () => {
    if (!wallet?.transactions) return { onlineEarnings: 0, pendingCommission: 0, netBalance: 0 };

    let onlineEarnings = 0;
    let pendingCommission = 0;

    wallet.transactions.forEach(transaction => {
      if (transaction.type === 'online_ride_earning') {
        onlineEarnings += transaction.amount;
      } else if (transaction.type === 'cod_commission_deduction') {
        pendingCommission += transaction.amount;
      }
    });

    const netBalance = onlineEarnings - pendingCommission;

    return {
      onlineEarnings,
      pendingCommission,
      netBalance
    };
  };

  const { onlineEarnings, pendingCommission, netBalance } = calculateCommissionBreakdown();

  // Use the actual wallet balance from database
  const databaseBalance = wallet.balance || 0;
  const calculatedCurrentBalance = databaseBalance;

  // If current balance is negative, it means commission is due
  // If current balance is positive, it's available for withdrawal

  const isNegativeBalance = calculatedCurrentBalance < 0;
  const isPendingWithdrawal = wallet.pendingWithdrawal?.amount > 0;
  const outstandingCommission = isNegativeBalance
    ? Math.abs(calculatedCurrentBalance)
    : 0;

  const handleWithdrawalRequest = async (withdrawalData) => {
    try {
      await dispatch(
        requestWithdrawal({
          driverId,
          ...withdrawalData,
        }),
      ).unwrap();
      setShowWithdrawalModal(false);
      onRefresh && onRefresh();
    } catch (error) {
      console.error("Withdrawal request failed:", error);
    }
  };

  const handleCommissionPayment = async (paymentData) => {
    try {
      await dispatch(
        payCommission({
          driverId,
          ...paymentData,
        }),
      ).unwrap();
      setShowCommissionModal(false);
      onRefresh && onRefresh();
    } catch (error) {
      console.error("Commission payment failed:", error);
    }
  };

  return (
    <>
      <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl shadow-md p-6 border border-blue-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100 text-blue-600 mr-3">
              <FaWallet className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800">My Wallet</h3>
          </div>
          <button
            onClick={() => setShowBalance(!showBalance)}
            className="p-2 rounded-full hover:bg-blue-200 transition-colors"
          >
            {showBalance ? (
              <FaEye className="w-4 h-4 text-blue-600" />
            ) : (
              <FaEyeSlash className="w-4 h-4 text-blue-600" />
            )}
          </button>
        </div>


        {/* Current Balance Section (primary) */}
        <div className="mb-6">
          <div className="flex items-center mb-2">
            <span className="text-sm text-gray-600 mr-2">Current Balance</span>
            {calculatedCurrentBalance >= 0 ? (
              <FaArrowUp className="w-4 h-4 text-green-500" />
            ) : (
              <FaArrowDown className="w-4 h-4 text-red-500" />
            )}
          </div>
          <div className="flex items-center">
            <span className={`text-3xl font-bold ${calculatedCurrentBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {showBalance ? formatCurrency(calculatedCurrentBalance) : "₹****"}
            </span>
          </div>
        </div>


        {/* Pending Withdrawal Alert */}
        {isPendingWithdrawal && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <div className="flex items-center">
              <FaClock className="w-5 h-5 text-yellow-500 mr-2" />
              <div>
                <p className="text-sm font-medium text-yellow-800">
                  Withdrawal Request Pending
                </p>
                <p className="text-sm text-yellow-700">
                  {formatCurrency(wallet.pendingWithdrawal.amount)} requested on{" "}
                  {new Date(
                    wallet.pendingWithdrawal.requestDate,
                  ).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3">
          {isNegativeBalance ? (
            <button
              onClick={() => setShowCommissionModal(true)}
              className="w-full bg-red-500 hover:bg-red-600 text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center"
            >
              <FaRupeeSign className="mr-2" />
              Pay Commission ({formatCurrency(outstandingCommission)})
            </button>
          ) : (
            !isPendingWithdrawal &&
            calculatedCurrentBalance > 0 && (
              <button
                onClick={() => navigate('/driver/wallet/withdraw')}
                className="w-full bg-green-500 hover:bg-green-600 text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center"
              >
                <FaArrowUp className="mr-2" />
                Withdraw Funds
              </button>
            )
          )}

          {/* Wallet Navigation Buttons */}
          <div className="grid grid-cols-2 gap-2 mb-2">
            <button
              onClick={() => navigate('/driver/wallet/history')}
              className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center"
            >
              <FaHistory className="mr-2" />
              History
            </button>
            <button
              onClick={() => navigate('/driver/wallet/withdraw')}
              className="bg-indigo-500 hover:bg-indigo-600 text-white py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center"
            >
              <FaArrowUp className="mr-2" />
              Withdraw
            </button>
          </div>

          {/* Commission Payment Button */}
          {isNegativeBalance && (
            <button
              onClick={() => navigate('/driver/wallet/commission')}
              className="w-full bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center mb-2"
            >
              <FaRupeeSign className="mr-2" />
              Pay Commission
            </button>
          )}

          <div className="space-y-2">
            <button
              onClick={() => {
                // Force refresh with cache busting
                if (onRefresh) {
                  onRefresh();
                  // Add a small delay and refresh again to ensure latest data
                  setTimeout(() => onRefresh(), 1000);
                }
              }}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-4 rounded-lg font-medium transition-colors"
            >
              Refresh Balance
            </button>

            {/* Sync Balance Button - show when balance needs refresh */}
            {false && (
              <button
                onClick={async () => {
                  try {
                    // Import API URL
                    const { API_URL } = await import('../../config/apiConfig');

                    // Get driver token
                    const token = localStorage.getItem('driverToken') ||
                                  localStorage.getItem('token');

                    // Call API to sync wallet balance
                    const response = await fetch(`${API_URL}/wallet/driver/${driverId}/sync-balance`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                      },
                      body: JSON.stringify({
                        calculatedBalance: calculatedCurrentBalance,
                        totalEarnings: wallet.totalEarnings || 0,
                        commissionPaid: wallet.totalCommissionPaid || 0
                      })
                    });

                    if (response.ok) {
                      if (onRefresh) onRefresh();
                      // Show success message
                    } else {
                      console.error('Failed to sync balance:', await response.text());
                    }
                  } catch (error) {
                    console.error('Failed to sync balance:', error);
                  }
                }}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg font-medium transition-colors mt-2"
              >
                Sync Database Balance
              </button>
            )}

            {/* Show sync warning if discrepancy detected */}
            {false && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-4 w-4 text-orange-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.487 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-2">
                    <p className="text-xs font-medium text-orange-800">
                      Data Sync Issue Detected
                    </p>
                    <p className="text-xs text-orange-700">
                      Wallet shows: {formatCurrency(wallet.totalEarnings || 0)}
                    </p>
                    <button
                      onClick={() => {
                        if (onRefresh) {
                          // Multiple refreshes to force sync
                          onRefresh();
                          setTimeout(() => onRefresh(), 500);
                          setTimeout(() => onRefresh(), 1500);
                        }
                      }}
                      className="mt-1 text-xs bg-orange-100 hover:bg-orange-200 text-orange-800 px-2 py-1 rounded"
                    >
                      Force Sync
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-3 mt-6 pt-6 border-t border-blue-200">
          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <FaArrowUp className="w-4 h-4 text-green-500 mr-1" />
              <span className="text-xs text-gray-600">Total Earnings</span>
            </div>
            <p className="font-semibold text-green-600 text-sm">
              {showBalance ? formatCurrency(wallet?.totalEarnings || 0) : "₹****"}
            </p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <FaArrowDown className="w-4 h-4 text-red-500 mr-1" />
              <span className="text-xs text-gray-600">Commission Paid</span>
            </div>
            <p className="font-semibold text-red-600 text-sm">
              {showBalance
                ? formatCurrency(wallet.totalCommissionPaid || wallet.calculatedCommission || 0)
                : "₹****"}
            </p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <FaArrowUp className="w-4 h-4 text-blue-500 mr-1" />
              <span className="text-xs text-gray-600">Withdrawals</span>
            </div>
            <p className="font-semibold text-blue-600 text-sm">
              {showBalance ? formatCurrency(wallet.totalWithdrawals || 0) : "₹****"}
            </p>
          </div>
        </div>
      </div>

      {/* Modals */}
      <WithdrawalModal
        isOpen={showWithdrawalModal}
        onClose={() => setShowWithdrawalModal(false)}
        onSubmit={handleWithdrawalRequest}
        maxAmount={calculatedCurrentBalance}
      />

      <CommissionPaymentModal
        isOpen={showCommissionModal}
        onClose={() => setShowCommissionModal(false)}
        onSubmit={handleCommissionPayment}
        commissionAmount={outstandingCommission}
      />
    </>
  );
};

export default WalletCard;
